/**
 * useZodForm — thin wrapper around react-hook-form's useForm with zodResolver pre-wired.
 *
 * Usage:
 *   const form = useZodForm({ schema: myZodSchema, defaultValues: { ... } });
 *   const { register, control, handleSubmit, formState: { errors } } = form;
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, type UseFormProps, type FieldValues, type Resolver } from 'react-hook-form'
import type { ZodType } from 'zod/v4'

interface UseZodFormOptions<TOutput extends FieldValues>
  extends Omit<UseFormProps<TOutput>, 'resolver'> {
  schema: ZodType<TOutput, TOutput>
}

/**
 * Creates a react-hook-form instance with a Zod schema resolver.
 * All standard useForm options are supported; the `resolver` is always set from `schema`.
 */
export function useZodForm<TOutput extends FieldValues>({
  schema,
  ...formOptions
}: UseZodFormOptions<TOutput>) {
  return useForm<TOutput>({
    ...formOptions,
    // Double-cast: first to unknown, then to the expected resolver type, to bridge
    // the gap between Zod 4's `unknown` input type and react-hook-form's FieldValues constraint
    resolver: zodResolver(schema as ZodType<TOutput, FieldValues>) as unknown as Resolver<TOutput>,
  })
}
